import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function CTA() {
  const { t } = useTranslation();

  const benefits = [
    t('cta.benefit1'),
    t('cta.benefit2'),
    t('cta.benefit3'),
    t('cta.benefit4')
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#FF6B6B]/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#4ECDC4]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Geometric Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-20 right-20 w-32 h-32 border-2 border-[#FF6B6B] rounded-full"></div>
        <div className="absolute bottom-40 left-20 w-24 h-24 border-2 border-[#4ECDC4] rounded-2xl rotate-12"></div>
        <div className="absolute top-1/2 right-1/3 w-16 h-16 border-2 border-[#A855F7] rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-[#4ECDC4] text-sm font-semibold mb-6">
              <i className="ri-rocket-fill"></i>
              {t('cta.badge')}
            </span>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('cta.title')}
              <br />
              <span className="bg-gradient-to-r from-[#FF6B6B] via-[#FF8E53] to-[#4ECDC4] bg-clip-text text-transparent">
                {t('cta.titleHighlight')}
              </span>
            </h2>

            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              {t('cta.description')}
            </p>

            {/* Benefits List */}
            <div className="space-y-4 mb-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white"></i>
                  </div>
                  <span className="text-white/90 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="group px-8 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-[#FF6B6B]/30 transition-all cursor-pointer whitespace-nowrap flex items-center gap-3"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('cta.ctaPrimary')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
              
              <Link
                to="/contact"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/20 flex items-center gap-3"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <i className="ri-file-text-line"></i>
                {t('cta.ctaSecondary')}
              </Link>
            </div>
          </div>

          {/* Right: Illustration */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/43fd98bdd3b79603ace9a9e2a339e32b.jpg"
                alt="Partenariat ABRS"
                className="w-full h-[600px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/80 via-transparent to-transparent"></div>
              
              {/* Floating Success Badge */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-2xl flex items-center justify-center">
                    <i className="ri-rocket-2-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('cta.deploymentTitle')}
                    </div>
                    <div className="text-gray-600 text-sm">{t('cta.deploymentDesc')}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-xl font-bold text-[#FF6B6B]" style={{ fontFamily: 'Poppins, sans-serif' }}>BRU</div>
                    <div className="text-gray-600 text-xs">Zaventem</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-[#4ECDC4]" style={{ fontFamily: 'Poppins, sans-serif' }}>CRL</div>
                    <div className="text-gray-600 text-xs">Charleroi</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-[#A855F7]" style={{ fontFamily: 'Poppins, sans-serif' }}>CMN</div>
                    <div className="text-gray-600 text-xs">Casablanca</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-3xl -z-10"></div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-full -z-10 opacity-50"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
